import React, { Fragment, useEffect, useReducer } from "react";
import { useDebouncedCallback } from "use-debounce";
import { toast } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";
import { Link } from "react-router-dom";

import ai from "../../helpers/axios";
import columns from "./click-to-fly-columns";
import TableBulkActions from "../table-bulk-actions";
import Search from "../search";
import DataTableServerPagination from "../data-table-server-pagination";
import PageHeaderRowSearch from "../shared/page-header-row-search";

const reducer = (state, action) => {
  switch (action.type) {
    case "search": {
      return {
        ...state,
        query: action.query,
      };
    }
    case "dataBeganDownloading": {
      return {
        ...state,
        loading: action.payload.loadingStatus,
      };
    }
    case "tableSort": {
      return {
        ...state,
        sortBy: action.payload.sortBy,
        sortDirection: action.payload.sortDirection,
      };
    }
    case "rowsDeleted": {
      return {
        ...state,
        clearSelectedRows: !state.clearSelectedRows,
        selectedRows: [],
      };
    }
    case "rowSelection": {
      return {
        ...state,
        selectedRows: action.payload.selectedRows,
        allSelected: action.payload.allSelected,
      };
    }
    case "dataDownloaded": {
      return {
        ...state,
        routes: {
          data: action.payload.data.routes,
        },
        loading: false,
        totalLength: action.payload.data.countTotal,
      };
    }
    case "pageChanged": {
      return {
        ...state,
        currentPage: action.payload.page,
      };
    }
    case "rowsPerPageChanged": {
      return {
        ...state,
        countPerPage: action.payload.currentRowsPerPage,
      };
    }
    default:
      break;
  }
  return state;
};

const ClickToFlyContent = () => {
  const initialState = {
    routes: {
      data: [],
    },
    loading: true,
    countPerPage: 25,
    currentPage: 1,
    query: "",
    selectedRows: [],
    allSelected: false,
    totalLength: null,
    clearSelectedRows: false,
    sortBy: "createdAt",
    sortDirection: "desc",
  };

  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [state, dispatch] = useReducer(reducer, initialState);

  const getData = async (dbQuery, loadSilently, sortBy, sortDirection) => {
    let loadingStatus = true;
    if (loadSilently) {
      loadingStatus = false;
    }

    dispatch({ type: "dataBeganDownloading", payload: { loadingStatus } });

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    let { data } = await ai.auth(token).get(`/api/clickToFly/list`, {
      params: {
        query: dbQuery,
        page: state.currentPage,
        countPerPage: state.countPerPage,
        sortBy,
        sortDirection,
      },
    });

    dispatch({
      type: "dataDownloaded",
      payload: {
        data,
      },
    });
  };

  useEffect(() => {
    getData("", false, state.sortBy, state.sortDirection);
    document.title = "ClickToFly Routes | evoJets HQ";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentPage, state.countPerPage]);

  const handlePageChange = (page, totalRows) => {
    dispatch({
      type: "pageChanged",
      payload: {
        page,
        totalRows,
      },
    });
  };

  const handleRowsPerPageChange = (currentRowsPerPage, currentPage) => {
    dispatch({
      type: "rowsPerPageChanged",
      payload: {
        currentRowsPerPage,
        currentPage,
      },
    });
  };

  const handleSelection = function (rowData) {
    dispatch({
      type: "rowSelection",
      payload: {
        selectedRows: rowData.selectedRows,
        allSelected: rowData.allSelected,
      },
    });
  };

  const handleDelete = async (e) => {
    e.preventDefault();

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    let rowIds = state.selectedRows.map((item) => item._id);
    let { data } = await ai
      .auth(token)
      .delete("/api/clickToFly/deleteMany", {
        data: { ids: rowIds },
      });

    if (data.deletedCount) {
      getData("", true, state.sortBy, state.sortDirection);

      toast.success("ClickToFly Routes Deleted Successfully", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }

    dispatch({
      type: "rowsDeleted",
    });
  };

  const debouncedGetData = useDebouncedCallback(getData, 500);

  const handleSearch = (e) => {
    let query = e.target.value.toLowerCase();
    dispatch({ type: "search", query });
    debouncedGetData(query, true, state.sortBy, state.sortDirection);
  };

  const handleSort = (column, sortDirection) => {
    dispatch({
      type: "tableSort",
      payload: {
        sortBy: column.selector,
        sortDirection,
      },
    });

    getData(state.query, true, column.selector, sortDirection);
  };

  return (
    <Fragment>
      <PageHeaderRowSearch
        h1="ClickToFly Routes"
        pretitle="Settings"
        breadcrumbs={[{ link: "/click-to-fly/routes", label: "ClickToFly Routes" }]}
        rightColumn={
          <div className="header-actions-row has-delete-btn">
            <Search onSearch={handleSearch} query={state.query}></Search>
            <TableBulkActions
              state={state}
              handleDelete={handleDelete}
              hideTags
              deleteBtnAlignment={"align-right"}
            />
            <Link to="/click-to-fly/routes/new" className="evo-blue base-button">
              Add New Route
            </Link>
          </div>
        }
      ></PageHeaderRowSearch>

      <DataTableServerPagination
        onSelection={handleSelection}
        columns={columns}
        handlePageChange={handlePageChange}
        handleRowsPerPageChange={handleRowsPerPageChange}
        state={state}
        data={state.routes.data}
        defaultSortField={"createdAt"}
        handleSort={handleSort}
        sortServer={true}
      ></DataTableServerPagination>
    </Fragment>
  );
};

export default ClickToFlyContent;

